import React from "react";
import { useNavigate } from "react-router-dom";
import { Notification } from "components/common/NotificationTypes";
import {
  Bell,
  BriefcaseBusiness,
  CheckCircle2,
  Eye,
  Heart,
  MessageCircle,
  Sparkles,
  UserPlus,
  Users,
} from "lucide-react";
import "assets/css/notifications.css";
import './NotificationItem.css';

// NotificationItem.tsx

export const FormattedContent: React.FC<{ notification: Notification }> = ({ notification }) => {
  const parts = notification.content.split(/(\*\*.*?\*\*)/g).filter(Boolean);

  return (
    <div>
      <p className="notification-main">
        <strong>{notification.user}</strong>{" "}
        {parts.map((part, index) => {
          const isStrong = part.startsWith("**") && part.endsWith("**");
          const text = isStrong ? part.slice(2, -2) : part;

          return isStrong ? (
            <strong key={`${text}-${index}`}>{text}</strong>
          ) : (
            <React.Fragment key={`${text}-${index}`}>{text}</React.Fragment>
          );
        })}
      </p>
    </div>
  );
};

function getNotificationIcon(type: Notification["type"], rawType?: string) {
  if (rawType === "profile_view") return Eye;
  switch (type) {
    case "job_application":
    case "job_post":
      return BriefcaseBusiness;
    case "connect":
      return UserPlus;
    case "follow":
      return Users;
    case "message":
      return MessageCircle;
    case "like":
      return Heart;
    case "generation":
      return Sparkles;
    case "subscription":
      return CheckCircle2;
    default:
      return Bell;
  }
}

function getNotificationLabel(notification: Notification) {
  if (notification.rawType === "profile_view") return "Profile view";
  if (notification.rawType === "job_application") {
    return notification.jobTitle ? "Application" : "Job";
  }

  switch (notification.type) {
    case "job_post":
      return "New job";
    case "follow":
      return "Follower";
    case "connect":
      return "Connection";
    case "message":
      return "Message";
    case "like":
      return "Reaction";
    case "generation":
      return "Match";
    case "subscription":
      return "Subscription";
    case "admin":
      return "Admin";
    default:
      return "Update";
  }
}

interface NotificationItemProps {
  notification: Notification;
  onRead?: (id: number) => void;
  /** If true, clicking closes the dropdown instead of navigating (used in preview panel) */
  onNavigate?: (url: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onRead,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const Icon = getNotificationIcon(notification.type, notification.rawType);

  const handleClick = () => {
    // Mark as read
    if (onRead) {
      onRead(notification.id);
    }

    const url = notification.navigationUrl || "/notifications";

    if (onNavigate) {
      onNavigate(url);
    } else {
      navigate(url);
    }
  };

  return (
    <div
      className={`notification-item${notification.isUnread ? " is-unread" : ""}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleClick(); }}
      style={{ cursor: "pointer" }}
    >
      <div className="notification-avatar-wrap">
        <img src={notification.avatarUrl} alt="" className="notification-avatar" />
        <span
          className="notification-type-icon"
          style={{ background: notification.badgeColor }}
        >
          <Icon size={11} strokeWidth={2.5} />
        </span>
      </div>

      <div className="notification-content">
        <FormattedContent notification={notification} />

        {notification.detail && (
          <p className="notification-detail">{notification.detail}</p>
        )}

        <div className="notification-meta">
          <span className="notification-time">{notification.timeAgo || "Just now"}</span>
          <span className="notification-chip">{getNotificationLabel(notification)}</span>
          {notification.isUnread && (
            <span className="notification-chip notification-unread-label">Unread</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
